import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { DURATION_QUICK, EASE_OUT, Tooltip } from '@strivelabs/ui-core/v2';

export interface ChatGroupHeaderProps {
  label: string;
  expanded: boolean;
  controls?: string;
  onToggle: () => void;
}

/**
 * One group's header: a fold toggle spanning the label, and a link out to the
 * full list. An expanded group only shows the chevron on hover or focus, since
 * the rows below already say it's open; a folded group keeps it visible. The
 * link out rests hidden so the heading stays a quiet divider until reached for.
 */
export function ChatGroupHeader({ label, expanded, controls, onToggle }: ChatGroupHeaderProps) {
  const reduce = useReducedMotion();

  return (
    <div className="group/header mt-2.5 flex w-full items-center gap-1 pr-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={controls}
        className="flex min-w-0 flex-1 items-center gap-1 py-1 text-left"
      >
        <span
          className={cn(
            'min-w-0 truncate text-[11px] font-medium uppercase leading-[18px] tracking-[0.07em]',
            'text-v2-sidebar-foreground/30',
          )}
        >
          {label}
        </span>
        <span
          aria-hidden
          className={cn(
            'grid size-4 shrink-0 place-items-center text-v2-sidebar-foreground/[0.62]',
            'transition-opacity duration-v2-quick',
            'group-hover/header:opacity-100 group-focus-visible/header:opacity-100',
            expanded ? 'opacity-0' : 'opacity-100',
          )}
        >
          <motion.span
            animate={{ rotate: expanded ? 90 : 0 }}
            initial={false}
            transition={reduce ? { duration: 0 } : { duration: DURATION_QUICK, ease: EASE_OUT }}
            className="grid place-items-center"
          >
            <ChevronRight size={12} strokeWidth={2.4} />
          </motion.span>
        </span>
      </button>
      <Tooltip label="View all chats" side="right" triggerClassName="shrink-0">
        <Link
          to="/ui/chats"
          aria-label="View all chats"
          className={cn(
            'grid size-4 shrink-0 place-items-center rounded-v2-sm text-v2-sidebar-foreground/[0.62]',
            'transition-opacity duration-v2-quick hover:text-v2-sidebar-foreground',
            'pointer-events-none opacity-0',
            'group-hover/header:pointer-events-auto group-hover/header:opacity-100',
            'focus-visible:pointer-events-auto focus-visible:opacity-100',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
            '[@media(pointer:coarse)]:pointer-events-auto [@media(pointer:coarse)]:opacity-100',
          )}
        >
          <ArrowUpRight size={12} strokeWidth={2.4} />
        </Link>
      </Tooltip>
    </div>
  );
}
